import React, { useCallback, useMemo, useState } from 'react';
import { Button, Divider, VisuallyHidden } from '@/components/ui';
import { DataTable } from '@/components/data-table';
import { FilePreviewModal } from '@/components/file-preview';
import { ROUTES } from '@/config/routes';
import { usePermissionAccess } from '@/hooks/usePermissionAccess';
import { PERMISSIONS } from '@/types/permissions';
import { StudyFile } from '@/types/studyFiles';
import { getStudyFileDownloadEndpoint } from '@/utils/downloadEndpoints';

import {
  IconArrowRight,
  IconFileCertificate,
  IconRefresh,
  IconSearch,
} from '@tabler/icons-react';
import { ColumnDef } from '@tanstack/react-table';

import StudyFileActions from './StudyFileActions';
import { StudyFileVersionsModal } from '@/components/file-versions';
import { StudyFilesEmptyState } from './StudyFilesEmptyState';
import { StudyFilesTableProps } from './types';
import { Link } from 'react-router-dom';

const StudyFilesTable: React.FC<StudyFilesTableProps> = ({
  files,
  loading = false,
  onDeleteFile,
  onRefresh,
  pagination,
  paginationState,
  onPaginationChange,
  actions,
  onUploadClick,
}) => {
  const [versionsModalOpen, setVersionsModalOpen] = useState(false);
  const [selectedFileForVersions, setSelectedFileForVersions] =
    useState<StudyFile | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [fileToPreview, setFileToPreview] = useState<StudyFile | null>(null);
  const hasWhitelistAccess = usePermissionAccess([
    PERMISSIONS.WHITELIST_ACCESS,
  ]);
  const canManageFiles = usePermissionAccess([PERMISSIONS.STUDY_FILES_ACCESS]);

  const handlePaginationChange = React.useCallback(
    (
      updater:
        | { pageIndex: number; pageSize: number }
        | ((
            prev: { pageIndex: number; pageSize: number }
          ) => { pageIndex: number; pageSize: number })
    ) => {
      if (!onPaginationChange) return;
      if (typeof updater === 'function') {
        const prev = paginationState ?? { pageIndex: 0, pageSize: 10 };
        onPaginationChange(updater(prev));
      } else {
        onPaginationChange(updater);
      }
    },
    [onPaginationChange, paginationState]
  );

  const handleShowVersions = React.useCallback((file: StudyFile) => {
    setSelectedFileForVersions(file);
    setVersionsModalOpen(true);
  }, []);

  const handleVersionsModalOpenChange = React.useCallback((open: boolean) => {
    setVersionsModalOpen(open);
    if (!open) {
      setSelectedFileForVersions(null);
    }
  }, []);

  const handlePreview = useCallback(
    (file: StudyFile) => {
      if (canManageFiles) {
        setFileToPreview(file);
        setPreviewModalOpen(true);
      }
    },
    [canManageFiles]
  );

  const handlePreviewModalChange = useCallback((open: boolean) => {
    setPreviewModalOpen(open);
    if (!open) {
      setFileToPreview(null);
    }
  }, []);

  // Debug: Log the generated API endpoint for preview
  const previewApiEndpoint = fileToPreview
    ? getStudyFileDownloadEndpoint(fileToPreview)
    : undefined;

  const enableColumnVisibility = true;
  const pageSizeOptions = useMemo(() => [10, 25, 50, 100], []);

  const columns: ColumnDef<StudyFile>[] = useMemo(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        meta: {
          cell: {
            variant: 'id',
          },
          minSize: 40,
          maxSize: 64,
        },
      },
      {
        accessorKey: 'filename',
        header: 'File Name',
        filterFn: 'includesString',
        meta: {
          cell: {
            variant: 'fileName',
            showIcon: true,
            truncate: true,
          },
          required: true,
          // size: 'auto',
          minSize: 400,
          maxSize: '1fr',
        },
      },
      {
        id: 'extension',
        accessorFn: row => {
          const filename = row.filename;
          if (!filename || typeof filename !== 'string') {
            return '';
          }
          const lastDot = filename.lastIndexOf('.');
          return lastDot > 0 ? filename.substring(lastDot + 1) : '';
        },
        header: 'Extension',
        enableSorting: false,
        meta: {
          cell: {
            variant: 'fileFormat',
          },
          minSize: 100,
          maxSize: 120,
        },
      },
      {
        accessorKey: 'size',
        header: 'Size',
        meta: {
          cell: {
            variant: 'fileSize',
          },
          minSize: 100,
          maxSize: 130,
        },
      },
      {
        accessorKey: 'content_type',
        header: 'Type',
        filterFn: 'includesString',
        meta: {
          cell: {
            variant: 'mimeType',
            showGroup: false,
          },
          minSize: 150,
          maxSize: 200,
          defaultVisible: false,
        },
      },
      {
        accessorKey: 'user',
        header: 'Uploaded by',
        enableSorting: false,
        meta: {
          cell: {
            variant: 'user',
            showEmail: true,
          },
          minSize: 200,
          maxSize: 280,
        },
      },
      {
        accessorKey: 'created_at',
        header: 'Uploaded',
        meta: {
          cell: {
            variant: 'datetime',
            // format: 'MMM D, YYYY h:mm A',
          },
          size: 'auto',
          // minSize: 160,
          // maxSize: 200,
        },
      },
      {
        id: 'actions',
        header: () => null,
        cell: ({ row }) => (
          <StudyFileActions
            file={row.original}
            onDelete={onDeleteFile}
            onShowVersions={handleShowVersions}
            onPreview={handlePreview}
          />
        ),
        enableSorting: false,
        enableHiding: false,
        enableColumnFilter: false,
        meta: {
          size: 'auto',
          systemColumn: true,
        },
      },
    ],
    [onDeleteFile, handleShowVersions, handlePreview]
  );

  const rightSlot = useMemo(
    () => (
      <>
        <VisuallyHidden>
          {hasWhitelistAccess && (
            <Button
              variant="outlined"
              size="small"
              disabled={!hasWhitelistAccess}
              startIcon={<IconFileCertificate />}
              endIcon={<IconArrowRight />}
              as={Link}
              to={ROUTES.whitelistManagement}
            >
              Manage Whitelist
            </Button>
          )}
          <Divider orientation="vertical" size="small" />
        </VisuallyHidden>
        <Button
          variant="outlined"
          size="small"
          onClick={onRefresh}
          startIcon={<IconRefresh />}
          disabled={loading}
        >
          Refresh
        </Button>
        {actions}
      </>
    ),
    [hasWhitelistAccess, onRefresh, loading, actions]
  );

  // TODO: When backend supports whitelist filtering, users with canManageStudyFiles
  // should see only files that are NOT in the whitelist for better management

  // Memoize pagination to avoid recreating on every render
  const paginationConfig = useMemo(
    () => ({
      pageSize: paginationState?.pageSize || 10,
      pageIndex: paginationState?.pageIndex || 0,
      pageCount: pagination?.total_pages || 0,
      totalCount: pagination?.total_count || 0,
      onPaginationChange: handlePaginationChange,
    }),
    [
      paginationState?.pageSize,
      paginationState?.pageIndex,
      pagination?.total_pages,
      pagination?.total_count,
      handlePaginationChange,
    ]
  );

  // Custom empty content for when no files exist
  const customEmptyContent = onUploadClick ? (
    <StudyFilesEmptyState onUploadClick={onUploadClick} />
  ) : undefined;

  return (
    <>
      <DataTable
        data={files}
        columns={columns}
        enableSorting={true}
        enableFiltering={true}
        enablePagination={true}
        manualPagination={true}
        enableColumnVisibility={enableColumnVisibility}
        enableRowSelection={false}
        loading={loading}
        searchPlaceholder="Search files..."
        pageSizeOptions={pageSizeOptions}
        toolbarLeftSlot={null}
        toolbarRightSlot={rightSlot}
        emptyMessage="No study files found"
        emptyIcon={<IconSearch />}
        customEmptyContent={customEmptyContent}
        getRowId={(row: StudyFile) => row.id.toString()}
        pagination={paginationConfig}
      />

      {/* Versions Modal */}
      {selectedFileForVersions && (
        <StudyFileVersionsModal
          file={selectedFileForVersions}
          open={versionsModalOpen}
          onOpenChange={handleVersionsModalOpenChange}
        />
      )}

      {/* File Preview Modal */}
      <FilePreviewModal
        key={`preview-${fileToPreview?.id || 'none'}`}
        apiEndpoint={previewApiEndpoint}
        filename={fileToPreview?.filename || ''}
        mimeType={fileToPreview?.content_type}
        fileSize={fileToPreview?.size}
        open={previewModalOpen && !!fileToPreview}
        onOpenChange={handlePreviewModalChange}
        title={fileToPreview ? `Preview: ${fileToPreview.filename}` : 'Preview'}
      />
    </>
  );
};

export default StudyFilesTable;
